<template>
  <div v-if="isMobile" class="mobile-message-widget">
    <!-- Mobile Header Bar -->
    <div 
      class="mobile-header-bar"
      @click="toggleWidget"
    >
      <div class="header-content">
        <font-awesome-icon :icon="['fas', 'comment']" class="chat-icon" />
        <span class="header-text">Messages</span>
        <span v-if="unreadCount > 0" class="unread-badge">{{ unreadCount }}</span>
      </div>
    </div>

    <!-- Full Screen Mobile Widget -->
    <div v-if="isExpanded" class="mobile-widget-fullscreen">
      <!-- Mobile Header -->
      <div class="mobile-widget-header">
        <button @click="toggleWidget" class="back-btn" title="Close Messages">
          <font-awesome-icon :icon="['fas', 'times']" />
        </button>
        <div class="header-title">
          <font-awesome-icon :icon="['fas', 'comment']" class="header-icon" />
          <span>Messages</span>
        </div>
      </div>

      <!-- Mobile Navigation -->
      <div class="mobile-nav">
        <button 
          @click="setActiveView('inbox')" 
          :class="['nav-btn', { active: activeView === 'inbox' }]"
        >
          <font-awesome-icon :icon="['fas', 'inbox']" />
          Inbox
          <span v-if="unreadCount > 0" class="nav-badge">{{ unreadCount }}</span>
        </button>
        <button 
          @click="setActiveView('compose')" 
          :class="['nav-btn', { active: activeView === 'compose' }]"
        >
          <font-awesome-icon :icon="['fas', 'pen']" />
          Compose
        </button>
      </div>

      <!-- Mobile Content -->
      <div class="mobile-content">
        <!-- Inbox View -->
        <div v-if="activeView === 'inbox'" class="mobile-inbox-view">
          <!-- Search Bar -->
          <div class="mobile-search-bar">
            <font-awesome-icon :icon="['fas', 'search']" class="search-icon" />
            <input 
              v-model="searchTerm"
              @input="searchMessages"
              type="text" 
              placeholder="Search messages..."
              class="mobile-search-input"
            />
          </div>

          <!-- Mobile Thread List -->
          <div class="mobile-message-list">
            <div 
              v-for="thread in filteredThreads" 
              :key="thread.id"
              @click="openThread(thread)"
              :class="['mobile-thread-item', { unread: thread.unreadCount > 0 }]"
            >
              <div class="mobile-thread-avatar">
                <font-awesome-icon :icon="['fas', 'user-md']" />
              </div>
              <div class="mobile-thread-content">
                <div class="mobile-thread-header">
                  <span class="mobile-participant-name">
                    <font-awesome-icon v-if="thread.isGroup" :icon="['fas', 'user-md']" class="group-icon" />
                    {{ thread.otherParticipant }}
                  </span>
                  <span class="mobile-thread-date">{{ formatDate(thread.lastMessage) }}</span>
                </div>
                <div v-if="thread.patientFocusName" class="mobile-thread-patient-focus">
                  [{{ thread.patientFocusName }}]
                </div>
                <div class="mobile-thread-preview-row">
                  <div
                    v-if="hasMessageAttachment(thread.lastMessage)"
                    class="mobile-thread-attachment-thumbs"
                  >
                    <message-attachment-preview
                      v-for="(att, attIdx) in normalizeMessageAttachments(thread.lastMessage).slice(0, 3)"
                      :key="'mt-' + thread.id + '-' + attIdx"
                      :attachment="att"
                      variant="compact"
                    />
                  </div>
                  <div class="mobile-thread-preview">{{ threadPreviewText(thread) }}</div>
                </div>
              </div>
              <div v-if="thread.unreadCount > 0" class="mobile-unread-badge">{{ thread.unreadCount }}</div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="filteredThreads.length === 0" class="mobile-empty-state">
            <font-awesome-icon :icon="['fas', 'inbox']" class="empty-icon" />
            <p>No conversations found</p>
          </div>
        </div>

        <!-- Compose View -->
        <div v-if="activeView === 'compose'" class="mobile-compose-view">
          <div class="mobile-compose-header">
            <button @click="setActiveView('inbox')" class="back-btn">
              <font-awesome-icon :icon="['fas', 'angle-left']" />
            </button>
            <span class="compose-title">New Message</span>
            <button @click="sendMessage" :disabled="!canSendMessage" class="send-btn">
              <font-awesome-icon :icon="['fas', 'plane-departure']" />
            </button>
          </div>

          <!-- Recipient Selection -->
          <div class="mobile-recipient-section">
            <div class="recipient-header">
              <span class="recipient-label">To:</span>
              <button @click="showContactSelection = true" class="add-recipients-btn">
                <font-awesome-icon :icon="['fas', 'plus-circle']" />
                Add Recipients
              </button>
            </div>
            <div v-if="selectedRecipients.length > 0" class="mobile-selected-recipients">
              <div 
                v-for="recipient in selectedRecipients" 
                :key="recipient.subaccountnumber"
                class="mobile-recipient-tag"
              >
                {{ recipient.name }}
                <button @click="removeRecipient(recipient)" class="remove-recipient-btn">
                  <font-awesome-icon :icon="['fas', 'times']" />
                </button>
              </div>
            </div>
          </div>

          <!-- Message Form -->
          <div class="mobile-message-form">
            <div v-if="canSelectPatientFocus" class="mobile-patient-focus">
              <!-- Pick a shared patient so this chat stays tied to them in the inbox. -->
              <label class="mobile-patient-focus-label">About patient (optional)</label>
              <select
                v-model="selectedPatientFocusId"
                class="mobile-patient-focus-select"
                :disabled="sharedPatientsLoading"
              >
                <option value="">None</option>
                <option
                  v-for="patient in sharedPatients"
                  :key="patient.id"
                  :value="patient.id"
                >
                  {{ patient.name }}
                </option>
              </select>
              <div v-if="!sharedPatientsLoading && sharedPatients.length === 0" class="mobile-patient-focus-hint">
                No shared patients with selected recipient(s).
              </div>
            </div>
            <div class="mobile-subject-row">
              <span v-if="selectedPatientFocusName" class="mobile-patient-focus-prefix">
                [{{ selectedPatientFocusName }}]
              </span>
              <input 
                v-model="newMessage.subject"
                type="text" 
                placeholder="Subject..."
                class="mobile-subject-input"
              />
            </div>
            <textarea 
              v-model="newMessage.message" 
              placeholder="Type your message..."
              class="mobile-message-textarea"
              rows="8"
            ></textarea>
            <div class="mobile-attachment-compose">
              <input
                ref="composeAttachmentInput"
                type="file"
                class="hidden-file-input"
                :accept="messageAttachmentAccept"
                multiple
                @change="onComposeAttachmentSelected"
              />
              <button
                type="button"
                class="mobile-attach-btn"
                @click="triggerComposeAttachmentPicker"
                :disabled="composeAttachmentFiles.length >= maxMessageAttachments"
              >
                <font-awesome-icon :icon="['fas', 'file']" />
                Attach files (max {{ maxMessageAttachments }})
              </button>
              <button
                v-if="composeAttachmentFiles.length"
                type="button"
                class="mobile-clear-attach-btn"
                @click="clearComposeAttachment"
              >
                Clear all
              </button>
              <!-- A phone that locks, or a tab that is left, pauses the upload. -->
              <p v-if="uploadsInFlight()" class="mobile-keep-open" data-test="keep-screen-open">
                Keep this screen open until the video finishes uploading.
              </p>
              <div v-if="composeAttachmentFiles.length" class="mobile-attachment-compose-list">
                <div
                  v-for="(file, fileIdx) in composeAttachmentFiles"
                  :key="'m-compose-' + fileIdx + '-' + file.name"
                  class="mobile-attachment-compose-item"
                >
                  <message-attachment-preview
                    :local-file="file"
                    :upload="videoUpload(file)"
                    variant="compose"
                    @upload-action="onVideoUploadAction(file, $event)"
                  />
                  <button
                    type="button"
                    class="mobile-clear-attach-btn"
                    @click="removeComposeAttachmentAt(fileIdx)"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Send Options -->
          <div class="mobile-send-options">
            <label class="mobile-checkbox-label">
              <input 
                v-model="sendSeparately" 
                type="checkbox" 
                class="mobile-checkbox"
              />
              <span>Send separately to each recipient</span>
            </label>
            <label class="mobile-checkbox-label">
              <input
                v-model="notifyRecipients"
                type="checkbox"
                class="mobile-checkbox"
              />
              <span>Notify recipients (email / SMS / phone)</span>
            </label>
            <button
              v-if="notifyRecipients"
              type="button"
              class="mobile-select-channels-btn"
              @click="openNotifyChannelsModal"
            >
              Select channels
            </button>
          </div>
        </div>

        <!-- Thread View -->
        <div v-if="activeView === 'thread'" class="mobile-thread-view">
          <!-- Thread Header -->
          <div class="mobile-thread-header">
            <button @click="setActiveView('inbox')" class="back-btn">
              <font-awesome-icon :icon="['fas', 'angle-left']" />
            </button>
            <div class="thread-info">
              <span class="thread-title">{{ selectedThread.otherParticipant }}</span>
              <span v-if="selectedThread.patientFocusName" class="thread-patient-focus">
                About [{{ selectedThread.patientFocusName }}]
              </span>
              <span class="thread-subtitle">{{ selectedThread.messages.length }} messages</span>
            </div>
            <button @click="showParticipants = !showParticipants" class="participants-btn">
              <font-awesome-icon :icon="['fas', 'user-md']" />
            </button>
          </div>

          <!-- Participants List (Collapsible) -->
          <div v-if="showParticipants" class="mobile-participants-list">
            <div class="participants-header">
              <span>Participants</span>
            </div>
            <div class="mobile-participants">
              <template v-if="selectedThread.isGroup && selectedThread.otherParticipantNames">
                <div class="mobile-participant-item current-user">
                  <font-awesome-icon :icon="['fas', 'user-md']" class="participant-icon" />
                  <span class="participant-name">You</span>
                </div>
                <div
                  v-for="(name, index) in selectedThread.otherParticipantNames"
                  :key="index"
                  class="mobile-participant-item"
                >
                  <font-awesome-icon :icon="['fas', 'user-md']" class="participant-icon" />
                  <span class="participant-name">{{ name }}</span>
                </div>
              </template>
              <template v-else>
                <div class="mobile-participant-item current-user">
                  <font-awesome-icon :icon="['fas', 'user-md']" class="participant-icon" />
                  <span class="participant-name">You</span>
                </div>
                <div class="mobile-participant-item">
                  <font-awesome-icon :icon="['fas', 'user-md']" class="participant-icon" />
                  <span class="participant-name">{{ selectedThread.otherParticipant }}</span>
                </div>
              </template>
            </div>
          </div>

          <!-- Chat Messages -->
          <div class="mobile-chat-messages">
            <div 
              v-for="message in sortedThreadMessages" 
              :key="message.id"
              :class="['mobile-message-bubble', { 'own-message': message.sender_id === messagingIdentity.subAccountNumber }]"
            >
              <div class="mobile-message-content">
                <!-- Show sender name for group messages if not sent by current user -->
                <div v-if="selectedThread.isGroup && message.sender_id != messagingIdentity.subAccountNumber" class="mobile-message-sender">
                  {{ getSenderName(message) }}
                </div>
                <div v-if="message.message && message.message !== '(Attachment)'" class="mobile-message-text">
                  {{ message.message }}
                </div>
                <div
                  v-if="hasMessageAttachment(message)"
                  class="mobile-message-attachments-list"
                >
                  <span
                    v-for="(att, attIdx) in normalizeMessageAttachments(message)"
                    :key="'m-bubble-' + (message.pd || '') + '-' + attIdx"
                    class="attachment-with-actions"
                  >
                    <message-attachment-preview :attachment="att" variant="bubble" />
                    <button
                      v-if="canRemoveAttachment(message, att)"
                      type="button"
                      class="attachment-remove"
                      data-test="attachment-remove"
                      @click.stop="openRemoveAttachment(message, att)"
                    >Remove</button>
                  </span>
                </div>
                <div class="mobile-message-timestamp">{{ formatDate(message) }}</div>
              </div>
            </div>
          </div>

          <!-- Quick Reply -->
          <div class="mobile-quick-reply">
            <form @submit.prevent="sendQuickReply" class="mobile-reply-form">
              <div class="mobile-reply-input-container">
                <input
                  ref="quickReplyAttachmentInput"
                  type="file"
                  class="hidden-file-input"
                  :accept="messageAttachmentAccept"
                  multiple
                  @change="onQuickReplyAttachmentSelected"
                />
                <button
                  type="button"
                  class="mobile-quick-attach-btn"
                  @click="triggerQuickReplyAttachmentPicker"
                  :disabled="quickReplyAttachmentFiles.length >= maxMessageAttachments"
                >
                  <font-awesome-icon :icon="['fas', 'file']" />
                </button>
                <div class="mobile-quick-reply-main">
                  <div class="mobile-quick-reply-notify-row">
                    <label class="mobile-checkbox-label mobile-quick-reply-notify">
                      <input
                        v-model="notifyRecipients"
                        type="checkbox"
                        class="mobile-checkbox"
                      />
                      <span>Notify recipients</span>
                    </label>
                    <button
                      v-if="notifyRecipients"
                      type="button"
                      class="mobile-select-channels-btn mobile-select-channels-btn--compact"
                      @click="openNotifyChannelsModal"
                    >
                      Select channels
                    </button>
                  </div>
                  <textarea 
                    v-model="quickReplyMessage" 
                    placeholder="Type your message..."
                    class="mobile-quick-reply-textarea"
                    rows="2"
                    @keydown.enter.exact.prevent="sendQuickReply"
                    @keydown.shift.enter.prevent="quickReplyMessage += '\n'"
                  ></textarea>
                </div>
                <button type="submit" class="mobile-quick-send-btn" :disabled="!canSendQuickReply">
                  <font-awesome-icon :icon="['fas', 'plane-departure']" />
                </button>
              </div>
              <p v-if="uploadsInFlight()" class="mobile-keep-open" data-test="keep-screen-open-reply">
                Keep this screen open until the video finishes uploading.
              </p>
              <div v-if="quickReplyAttachmentFiles.length" class="mobile-quick-reply-attachment">
                <div
                  v-for="(file, fileIdx) in quickReplyAttachmentFiles"
                  :key="'m-qr-' + fileIdx + '-' + file.name"
                  class="mobile-attachment-compose-item"
                >
                  <message-attachment-preview
                    :local-file="file"
                    :upload="videoUpload(file)"
                    variant="compose"
                    @upload-action="onVideoUploadAction(file, $event)"
                  />
                  <button
                    type="button"
                    class="mobile-clear-attach-btn"
                    @click="removeQuickReplyAttachmentAt(fileIdx)"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Contact Selection Modal -->
      <div v-if="showContactSelection" class="mobile-contact-modal">
        <div class="mobile-modal-overlay" @click="showContactSelection = false"></div>
        <div class="mobile-modal-content">
          <div class="mobile-modal-header">
            <button @click="showContactSelection = false" class="close-modal-btn">
              <font-awesome-icon :icon="['fas', 'times']" />
            </button>
            <span class="modal-title">Select Recipients</span>
            <button @click="confirmSelection" class="confirm-btn">
              Add Recipients
            </button>
          </div>
          <contact-selection-list 
            @contacts-selected="onContactsSelected"
            @group-selected="onPatientGroupSelected"
            class="mobile-contact-list"
          />
        </div>
      </div>

      <notify-channels-modal
        v-if="showNotifyChannelsModal"
        fixed
        :rows="notifyChannelModalRows"
        :draft="notifyChannelDraft"
        :loading="notifyChannelsLoading"
        @cancel="cancelNotifyChannels"
        @save="saveNotifyChannels"
        @update-draft="onNotifyChannelDraftUpdate"
      />
    </div>
    <!-- One confirmation per widget for removing a sent attachment. -->
    <remove-file-dialog
      :show="!!removeAttachmentTarget"
      :file-name="removeAttachmentTarget ? removeAttachmentTarget.name : ''"
      :busy="removeAttachmentBusy"
      :error-text="removeAttachmentError"
      @confirm="confirmRemoveAttachment"
      @cancel="cancelRemoveAttachment"
    />
  </div>
</template>

<script>
import { CURR_API } from "@/constants/apis";
import axios from "axios";
import { mapGetters } from "vuex";
import { refusalMessage } from "@/api/refusals";
import ContactSelectionList from "./ContactSelectionList.vue";
import MessageAttachmentPreview from "./MessageAttachmentPreview.vue";
import RemoveFileDialog from "@/components/includes/RemoveFileDialog.vue";
import NotifyChannelsModal from "./NotifyChannelsModal.vue";
import messageAttachmentMixin from "./messageAttachmentMixin.js";
import notifyChannelsComposeMixin from "./notifyChannelsComposeMixin.js";
import { usePrivilegesStore } from "@/stores/privileges";
import { useSessionSelfStore } from "@/stores/sessionSelf";
import { PRIVILEGES } from "@/constants/privileges";
import { assertPrivilegeOrAlert } from "@/utils/privilegeGate";
import { messagingIdentityFrom } from "@/utils/sessionIdentity";
import { formatMessageWhen } from "@/utils/messageTimestamp";

export default {
  name: "MobileMessageWidget",
  mixins: [messageAttachmentMixin, notifyChannelsComposeMixin],
  components: {
    ContactSelectionList,
    MessageAttachmentPreview,
    RemoveFileDialog,
    NotifyChannelsModal,
  },
  data() {
    return {
      isExpanded: false,
      activeView: 'inbox', // 'inbox', 'compose', 'thread'
      
      // Messages
      messages: [],
      filteredMessages: [],
      conversationThreads: [], // Grouped conversation threads
      filteredThreads: [], // Filtered conversation threads
      selectedThread: null, // Selected conversation thread
      selectedMessage: null,
      searchTerm: '',
      
      // Compose
      newMessage: {
        searchTerm: '',
        subject: '',
        message: ''
      },
      selectedRecipients: [],
      searchResults: [],
      showContactSelection: false,
      sendSeparately: false, // Default to current behavior
      notifyRecipients: false,
      // Optional "this chat is about patient X" picker state (1:1 only).
      sharedPatients: [],
      sharedPatientsLoading: false,
      selectedPatientFocusId: '',
      
      // Reply
      isReplying: false,
      replyMessage: '',
      quickReplyMessage: '', // For thread quick reply
      showParticipants: false,
      
      // Contact search
      searchTimeout: null,
      
      // Debug
      screenWidth: window.innerWidth
    };
  },

  watch: {
    selectedRecipients: {
      handler() {
        this.refreshSharedPatients();
      },
      deep: true,
    },
    sendSeparately() {
      this.refreshSharedPatients();
    },
  },
  
  computed: {
    ...mapGetters("login", {
      allData: "getAllData",
    }),
    
    unreadCount() {
      // Calculate unread count from conversation threads instead of raw messages
      return this.conversationThreads.reduce((total, thread) => total + thread.unreadCount, 0);
    },
    
    hasUnreadMessages() {
      return this.unreadCount > 0;
    },

    canSelectPatientFocus() {
      // 1:1 / send-separately only; skip when a recipient is already a patient.
      if (!this.selectedRecipients.length) return false;
      if (this.selectedRecipients.length > 1 && !this.sendSeparately) return false;
      const hasPatientRecipient = this.selectedRecipients.some((r) => {
        const t = String(r.accountType || '').toLowerCase();
        return t === 'patient' || t === 'patients';
      });
      return !hasPatientRecipient;
    },

    selectedPatientFocusName() {
      if (!this.selectedPatientFocusId) return '';
      const match = this.sharedPatients.find((p) => String(p.id) === String(this.selectedPatientFocusId));
      return match ? match.name : '';
    },

    // Subject as stored/sent: "[Patient Name] typed subject"
    composedSubject() {
      const body = (this.newMessage.subject || '').trim();
      if (!this.selectedPatientFocusName) return body;
      return `[${this.selectedPatientFocusName}] ${body}`.trim();
    },
    
    // Require subject + (text body or at least one attachment).
    canSendMessage() {
      const hasBody = !!this.newMessage.message.trim() || this.composeAttachmentFiles.length > 0;
      return this.selectedRecipients.length > 0 && 
             !!this.newMessage.subject.trim() && 
             hasBody &&
             !this.uploadsBusy('compose');
    },

    // A video that is still uploading (or failed) holds the send of its own box.
    canSendQuickReply() {
      return (!!this.quickReplyMessage.trim() || this.quickReplyAttachmentFiles.length > 0) &&
             !this.uploadsBusy('quickReply');
    },
    
    sortedThreadMessages() {
      if (!this.selectedThread || !this.selectedThread.messages) {
        console.log('No selected thread or messages:', this.selectedThread);
        return [];
      }
      
      console.log('Thread messages count:', this.selectedThread.messages.length);
      
      // Sort messages chronologically (oldest first) for chat view using full timestamp
      return [...this.selectedThread.messages].sort((a, b) => {
        const dateA = new Date(a.msgTimestamp || a.msgDate);
        const dateB = new Date(b.msgTimestamp || b.msgDate);
        return dateA - dateB; // Oldest first
      });
    },
    
    isMobile() {
      return this.screenWidth <= 768;
    },
    canSendInternalMessages() {
      return usePrivilegesStore().effectiveGranted(PRIVILEGES.SEND_INTERNAL_MESSAGES);
    },

    messagingIdentity() {
      return messagingIdentityFrom(
        this.allData,
        useSessionSelfStore().profile
      );
    },
  },
  
  async mounted() {
    console.log('MobileMessageWidget mounted');
    console.log('Screen width:', window.innerWidth);
    console.log('Is mobile:', window.innerWidth <= 768);
    
    // Add resize listener
    window.addEventListener('resize', this.handleResize);
    
    await this.fetchMessages();
    // Check for new messages every 30 seconds
    this.messageInterval = setInterval(this.fetchMessages, 30000);
  },
  
  beforeUnmount() {
    if (this.messageInterval) {
      clearInterval(this.messageInterval);
    }
    window.removeEventListener('resize', this.handleResize);
  },
  
  methods: {
    // Debug method
    handleResize() {
      this.screenWidth = window.innerWidth;
    },
    
    // Widget Controls
    toggleWidget() {
      console.log('Toggle widget called, current isExpanded:', this.isExpanded);
      const willExpand = !this.isExpanded;
      if (
        willExpand &&
        !assertPrivilegeOrAlert(
          PRIVILEGES.SEND_INTERNAL_MESSAGES,
          "You do not have permission to use internal messages."
        )
      ) {
        return;
      }
      this.isExpanded = willExpand;
      if (this.isExpanded) {
        this.setActiveView('inbox');
        // Force dashboard to be hidden when widget is expanded
        this.hideDashboard();
      } else {
        // Show dashboard when widget is collapsed
        this.showDashboard();
      }
      console.log('Widget toggled, new isExpanded:', this.isExpanded);
    },
    
    minimizeWidget() {
      this.isExpanded = false;
    },
    
    closeWidget() {
      this.isExpanded = false;
    },
    
    // Dashboard control methods
    hideDashboard() {
      const dashboard = document.querySelector('.dashboard');
      if (dashboard) {
        dashboard.style.zIndex = '-1';
        dashboard.style.position = 'relative';
        dashboard.style.display = 'none';
        dashboard.style.visibility = 'hidden';
        dashboard.style.opacity = '0';
      }
      
      // Also hide all dashboard children
      const dashboardChildren = document.querySelectorAll('.dashboard *');
      dashboardChildren.forEach(child => {
        child.style.zIndex = '-1';
        child.style.position = 'relative';
      });
    },
    
    showDashboard() {
      const dashboard = document.querySelector('.dashboard');
      if (dashboard) {
        dashboard.style.zIndex = '1';
        dashboard.style.position = 'relative';
        dashboard.style.display = 'block';
        dashboard.style.visibility = 'visible';
        dashboard.style.opacity = '1';
      }
    },
    
    setActiveView(view) {
      if (this.viewChangeBlocked()) return;
      if (
        view === "compose" &&
        !assertPrivilegeOrAlert(
          PRIVILEGES.SEND_INTERNAL_MESSAGES,
          "You do not have permission to compose internal messages."
        )
      ) {
        return;
      }
      this.activeView = view;
      if (view === 'compose') {
        this.clearMessage();
      }
    },
    
    // Message Management
    async fetchMessages() {
      try {
        // Fetching is allowed regardless of UI privileges; the widget still blocks usage.
        // Guard only on identity availability (common on first mount before session loads).
        if (!this.messagingIdentity.accountNumber || !this.messagingIdentity.subAccountNumber) {
          return;
        }
        const content = {
          Messages: {
            API_Meth: "FetchAllMessagesByIDS",
            accountnumber: this.messagingIdentity.accountNumber,
            subaccountnumber: this.messagingIdentity.subAccountNumber,
          },
        };
        
        const response = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        
        if (response.data && response.data.records) {
          // Remove duplicates based on message ID (pd field)
          const uniqueMessages = response.data.records.filter((message, index, self) => 
            index === self.findIndex(m => m.pd === message.pd)
          );
          
          // Sort messages chronologically (newest first) using full timestamp
          this.messages = uniqueMessages.sort((a, b) => {
            const dateA = new Date(a.msgTimestamp || a.msgDate);
            const dateB = new Date(b.msgTimestamp || b.msgDate);
            return dateB - dateA; // Newest first
          });
          
          // console.log(`Original messages: ${response.data.records.length}, After deduplication: ${this.messages.length}`);
          
          // Debug: Check message statuses
          const statusCounts = {};
          this.messages.forEach(msg => {
            statusCounts[msg.msgStatus] = (statusCounts[msg.msgStatus] || 0) + 1;
          });
          // console.log('Message status counts:', statusCounts);
          
          // Group messages into conversation threads
          this.groupMessagesByThread();
        }
      } catch (error) {
        console.error('Error fetching messages:', error);
      }
    },
    
    groupMessagesByThread() {
      // Group messages by conversation participants (individual and group)
      const threads = new Map();
      const currentUser = this.messagingIdentity.subAccountNumber; // Current messaging subaccount number
      
      this.messages.forEach(message => {
        // Process the message
        
        // Handle group messages
        if (message.is_group_message == 1 && message.group_id) {
          const threadKey = `group_${message.group_id}`;
          
          if (!threads.has(threadKey)) {
            // Parse participants from group_participants field
            const participantIds = message.group_participants ? message.group_participants.split(',').map(id => id.trim()) : [];

            // Keep id+name paired (notify channel modal must not zip separate lists).
            const participantsWithNames = participantIds.map((id) => {
              const participantMessage = this.messages.find(m =>
                (m.sender_id == id && m.sender_name) || (m.recipient_id == id && m.recipient_name)
              );
              let name = `User ${id}`;
              if (participantMessage) {
                name = participantMessage.sender_id == id
                  ? participantMessage.sender_name
                  : participantMessage.recipient_name;
              }
              return { id, name };
            });
            const otherParticipants = participantsWithNames.filter(
              (p) => String(p.id) !== String(currentUser)
            );
            const participantNames = participantsWithNames
              .map((p) => p.name)
              .filter((name, index, arr) => arr.indexOf(name) === index);
            const otherParticipantNames = otherParticipants.map((p) => p.name);

            // Create display name from other participants
            let displayName = otherParticipantNames.join(', ');
            
            // Truncate if too long (more than 30 characters)
            if (displayName.length > 30) {
              displayName = displayName.substring(0, 27) + '...';
            }
            
            // Fallback if no other participants found
            if (!displayName || displayName.trim() === '') {
              displayName = `Group Chat (${participantNames.length} members)`;
            }
            
            threads.set(threadKey, {
              id: threadKey,
              isGroup: true,
              groupId: message.group_id,
              participants: participantIds,
              participantNames: participantNames,
              otherParticipants: otherParticipants,
              otherParticipantNames: otherParticipantNames, // For the expandable list
              messages: [],
              lastMessage: null,
              unreadCount: 0,
              otherParticipant: displayName,
              otherParticipantId: null,
              patientFocusId: null,
              patientFocusName: null,
              seenMessages: new Set() // Track unique messages by group_id + msgTimestamp
            });
          }
          
          const thread = threads.get(threadKey);
          
          // Deduplicate group messages: only add if we haven't seen this exact message before
          const messageKey = `${message.group_id}_${message.msgTimestamp}_${message.message}`;
          if (!thread.seenMessages.has(messageKey)) {
            thread.seenMessages.add(messageKey);
            thread.messages.push(message);
            
            // Update last message
            const messageTime = new Date(message.msgTimestamp || message.msgDate);
            const lastMessageTime = thread.lastMessage ? new Date(thread.lastMessage.msgTimestamp || thread.lastMessage.msgDate) : null;
            
            if (!thread.lastMessage || messageTime > lastMessageTime) {
              thread.lastMessage = message;
            }
          }
          
          // Count unread messages for group chats (only if current user is recipient)
          if (message.recipient_id == currentUser && message.msgStatus === 'NEW') {
            thread.unreadCount++;
          }
          
        } else {
          // Handle individual messages (existing logic)
          let otherParticipantId, otherParticipantName;
          
          if (message.sender_id == currentUser) {
            // Current user sent this message, other participant is the recipient
            otherParticipantId = message.recipient_id;
            otherParticipantName = message.recipient_name;
          } else if (message.recipient_id == currentUser) {
            // Current user received this message, other participant is the sender
            otherParticipantId = message.sender_id;
            otherParticipantName = message.sender_name;
          } else {
            // This shouldn't happen if our query is correct, but handle it
            console.warn(`Message doesn't involve current user: sender=${message.sender_id}, recipient=${message.recipient_id}, current=${currentUser}`);
            return; // Skip this message
          }
          
          // Get other participant info
          
          // Split 1:1 threads by pair + patient focus so chats about different patients stay separate.
          const focusKey = message.patient_focus_id ? String(message.patient_focus_id) : 'none';
          const participants = [currentUser, otherParticipantId].sort().join('-') + '::' + focusKey;
          
          if (!threads.has(participants)) {
            threads.set(participants, {
              id: participants,
              isGroup: false,
              participants: [currentUser, otherParticipantId],
              messages: [],
              lastMessage: null,
              unreadCount: 0,
              otherParticipant: otherParticipantName,
              otherParticipantId: otherParticipantId,
              patientFocusId: message.patient_focus_id || null,
              patientFocusName: message.patient_focus_name || null
            });
          }
          
          const thread = threads.get(participants);
          thread.messages.push(message);
          if (!thread.patientFocusName && message.patient_focus_name) {
            thread.patientFocusName = message.patient_focus_name;
          }
          
          // Update last message (most recent) using full timestamp
          const messageTime = new Date(message.msgTimestamp || message.msgDate);
          const lastMessageTime = thread.lastMessage ? new Date(thread.lastMessage.msgTimestamp || thread.lastMessage.msgDate) : null;
          
          if (!thread.lastMessage || messageTime > lastMessageTime) {
            thread.lastMessage = message;
          }
          
          // Count unread messages
          if (message.msgStatus === 'NEW') {
            thread.unreadCount++;
          }
        }
      });
      
      // Convert to array and sort by last message timestamp (newest first)
      const threadsArray = Array.from(threads.values());
      this.conversationThreads = threadsArray.sort((a, b) => {
        const dateA = new Date(a.lastMessage.msgTimestamp || a.lastMessage.msgDate);
        const dateB = new Date(b.lastMessage.msgTimestamp || b.lastMessage.msgDate);
        return dateB - dateA; // Newest first
      });
      
      // Debug: Log unread counts for each thread
      // Calculate unread counts
      
      this.filteredThreads = [...this.conversationThreads];
    },
    
    sortConversationThreads() {
      // Sort by most recent message first
      this.conversationThreads.sort((a, b) => {
        const dateA = new Date(a.lastMessage?.msgDate || 0);
        const dateB = new Date(b.lastMessage?.msgDate || 0);
        return dateB - dateA; // Most recent first
      });
      this.filteredThreads = [...this.conversationThreads];
    },
    
    searchMessages() {
      if (!this.searchTerm.trim()) {
        this.filteredThreads = [...this.conversationThreads];
        return;
      }
      
      const term = this.searchTerm.toLowerCase();
      this.filteredThreads = this.conversationThreads.filter(thread => 
        // Search in participant name
        (thread.otherParticipant && thread.otherParticipant.toLowerCase().includes(term)) ||
        (thread.patientFocusName && thread.patientFocusName.toLowerCase().includes(term)) ||
        // Search in any message content within the thread
        thread.messages.some(message => 
          (message.subject && message.subject.toLowerCase().includes(term)) ||
          (message.message && message.message.toLowerCase().includes(term))
        )
      );
    },
    
    async openThread(thread) {
      this.selectedThread = thread;
      this.setActiveView('thread');
      
      // Collect all unread message IDs in this thread
      const unreadMessageIds = thread.messages
        .filter(message => message.msgStatus === 'NEW')
        .map(message => message.pd);
      
      // Mark messages as read
      
      // Mark messages as read in database first
      if (unreadMessageIds.length > 0) {
        // Mark messages as read
        const success = await this.markMessagesAsRead(unreadMessageIds);
        
        if (success) {
          // Only update frontend state after successful API call
          thread.messages.forEach(message => {
            if (message.msgStatus === 'NEW') {
              message.msgStatus = 'read'; // Use lowercase to match database
            }
          });
          
          // Reset unread count
          thread.unreadCount = 0;
          
          // Re-sort conversation list since unread count changed
          this.sortConversationThreads();
          
          // Update unread count after marking messages as read
          // Update unread count after marking messages as read
          
          // Emit global event to update notification count
          this.$vueEventBus.$emit('update-notification-count', this.unreadCount);
        }
      }
      
      // Scroll to bottom after DOM update
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },
    
    async markMessagesAsRead(messageIds) {
      try {
        const formData = new FormData();
        formData.append("API_Meth", "MarkMessagesAsRead");
        formData.append("message_ids", messageIds.join(','));
        formData.append("user_id", this.messagingIdentity.subAccountNumber);
        formData.append("account_id", this.messagingIdentity.accountNumber);
        
        const response = await axios.post(CURR_API + "/tswebhook.php", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        
        console.log('Mark as read response:', response.data);
        
        if (response.data && response.data.status === "success") {
          console.log(`Successfully marked ${response.data.updated_count} messages as read`);
          return true;
        } else {
          throw new Error(response.data?.message || 'Unknown error marking messages as read');
        }
      } catch (error) {
        console.error('Error marking messages as read:', error);
        alert(`Error marking messages as read: ${error.message}`);
        return false;
      }
    },
    
    getSenderName(message) {
      // Get sender name for group messages
      if (message.sender_name) {
        return message.sender_name;
      }
      // Fallback to participant ID if name not available
      return `User ${message.sender_id}`;
    },
    
    // Contact Selection
    onContactsSelected(contacts) {
      const incoming = Array.isArray(contacts) ? contacts : [];
      const normalized = incoming.map((contact) => {
        const stableId =
          contact.subAccountNumber ||
          contact.subaccountnumber ||
          contact.id ||
          contact.unique_ID ||
          contact.email ||
          `${contact.name || 'recipient'}-${Math.random()}`;
        return {
          id: String(stableId),
          subaccountnumber: String(stableId),
          name: contact.name || "",
          email: contact.email || "",
          accountType: contact.accountType || "",
          isExternal: !!contact.isExternal,
        };
      });
      const unique = [];
      const seen = new Set();
      normalized.forEach((recipient) => {
        const key = `${recipient.id}::${recipient.name}`;
        if (seen.has(key)) return;
        seen.add(key);
        unique.push(recipient);
      });
      this.selectedRecipients = unique;
      this.showContactSelection = false;
    },
    
    onPatientGroupSelected(group) {
      // Add all members of the group to recipients
      if (group && group.members) {
        const groupMembers = group.members.map(member => ({
          subaccountnumber: member.subaccountnumber,
          name: member.name,
          email: member.email,
          accountType: member.accountType
        }));
        
        this.selectedRecipients = [...this.selectedRecipients, ...groupMembers];
      }
      this.showContactSelection = false;
    },
    
    removeRecipient(recipient) {
      this.selectedRecipients = this.selectedRecipients.filter(r => r.subaccountnumber !== recipient.subaccountnumber);
    },

    // Load patients both sender + recipients are assigned to (compose dropdown).
    async refreshSharedPatients() {
      this.selectedPatientFocusId = '';
      this.sharedPatients = [];

      if (!this.canSelectPatientFocus) {
        return;
      }

      const recipientIds = this.selectedRecipients
        .map((r) => String(r.subaccountnumber || r.id || '').trim())
        .filter(Boolean);

      if (!recipientIds.length) return;

      this.sharedPatientsLoading = true;
      try {
        const content = {
          Messages: {
            API_Meth: "GetSharedPatientsForMessaging",
            accountnumber: this.messagingIdentity.accountNumber,
            requester_unique_ID: this.messagingIdentity.subAccountNumber,
            other_unique_IDs: recipientIds,
          },
        };
        const response = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        if (response.data && String(response.data.status).toLowerCase() === "successful") {
          this.sharedPatients = Array.isArray(response.data.data) ? response.data.data : [];
        } else {
          this.sharedPatients = [];
        }
      } catch (error) {
        console.error('Error loading shared patients:', error);
        this.sharedPatients = [];
      } finally {
        this.sharedPatientsLoading = false;
      }
    },
    
    confirmSelection() {
      this.showContactSelection = false;
    },
    
    // Message Sending
    async sendMessage() {
      // Not while a video is still going up, and not twice at once.
      if (this.uploadsBusy('compose') || !this.beginSend('compose')) return;
      try {
        if (this.sendSeparately || this.selectedRecipients.length === 1) {
          // Send separately to each recipient (current behavior)
          await this.sendIndividualMessages();
        } else {
          // Create group chat (new behavior)
          await this.sendGroupMessage();
        }
      } catch (error) {
        console.error('Error sending message:', error);
        // A refusal answered with an error status (413 for an oversized file,
        // 503 while uploads are paused) arrives here rather than as a response.
        alert(refusalMessage(error?.response?.data) || "Error sending message. Please try again.");
      } finally {
        this.endSend('compose');
      }
    },

    // Send individual messages to each recipient (existing behavior)
    async sendIndividualMessages() {
      console.log('Sending individual messages to:', this.selectedRecipients);
      
      // Send one message per recipient (separate conversations)
      let successCount = 0;
      // The first refusal in the server's words. Every recipient gets the same
      // attachment, so a paused or oversized upload refuses them all alike.
      let refusal = null;
      // A video is uploaded once and named by uid in each message. If a message
      // goes out without it (an older replica ignored the field), the rest are
      // not sent into the same server, and those that still need it stay selected.
      const videoUids = this.composeVideoUids();
      const completed = new Set();
      let videoMissing = false;
      
      for (const recipient of this.selectedRecipients) {
        const formData = new FormData();
        formData.append("API_Meth", "SendInternalMessages");
        
        // Send to ONE recipient at a time
        formData.append("recieversubaccountnumber", recipient.subaccountnumber);
        formData.append("sendersubaccountnumber", this.messagingIdentity.subAccountNumber);
        formData.append("senderaccountnunber", this.messagingIdentity.accountNumber);
        formData.append("additionalcontacts", recipient.email || '');
        formData.append("internalcontacts", recipient.subaccountnumber);
        formData.append("message", this.composeMessageBody());
        formData.append("subject", this.composedSubject);
        formData.append("status", "NEW");
        formData.append("senderName", this.messagingIdentity.senderName);
        if (this.canSelectPatientFocus && this.selectedPatientFocusId) {
          formData.append("patient_focus_id", this.selectedPatientFocusId);
        }
        this.appendComposeAttachment(formData);
        if (this.notifyRecipients) {
          formData.append("notify_recipients", "1");
          this.appendNotifyChannels(formData, [recipient]);
        }
        
        try {
          const response = await axios.post(CURR_API + "/tswebhook.php", formData);
          if (response.data.message === "Inserted") {
            if (!this.verifyVideoAttached(response.data, videoUids)) {
              videoMissing = true;
              break;
            }
            successCount++;
            completed.add(recipient);
            // Message sent successfully
          } else {
            refusal = refusal || refusalMessage(response.data);
            console.error(`❌ Failed to send to ${recipient.name}:`, response.data);
          }
        } catch (error) {
          refusal = refusal || refusalMessage(error?.response?.data);
          console.error(`❌ Error sending to ${recipient.name}:`, error);
        }
      }
      
      if (videoMissing) {
        // The composer, its text and the uploaded video stay for a second Send.
        this.selectedRecipients = this.selectedRecipients.filter((recipient) => !completed.has(recipient));
        return;
      }
      if (successCount > 0) {
        alert(`Message sent separately to ${successCount}/${this.selectedRecipients.length} recipient${this.selectedRecipients.length !== 1 ? 's' : ''}!`);
        this.clearMessage();
        this.setActiveView('inbox');
        await this.fetchMessages();
        this.sortConversationThreads();
      } else {
        alert(refusal || "Error sending messages. Please try again.");
      }
    },

    // Send group message (new behavior)
    async sendGroupMessage() {
      console.log('Sending group message to:', this.selectedRecipients);
      
      const formData = new FormData();
      formData.append("API_Meth", "SendGroupMessage");
      
      // Prepare participants list (ONLY recipients, NOT sender)
      const recipientIds = this.selectedRecipients.map(r => r.subaccountnumber);
      const senderSubaccnt = this.messagingIdentity.subAccountNumber;
      
      // For the database, we include sender in participants list for grouping
      // But we only send messages to recipients, not to sender
      const allParticipants = [senderSubaccnt, ...recipientIds];
      
      formData.append("sendersubaccountnumber", senderSubaccnt);
      formData.append("senderaccountnunber", this.messagingIdentity.accountNumber);
      formData.append("participants", recipientIds.join(',')); // Only send to recipients
      formData.append("all_participants", allParticipants.join(',')); // For grouping purposes
      formData.append("message", this.composeMessageBody());
      formData.append("subject", this.newMessage.subject);
      formData.append("status", "NEW");
      formData.append("senderName", this.messagingIdentity.senderName);
      this.appendComposeAttachment(formData);
      if (this.notifyRecipients) {
        formData.append("notify_recipients", "1");
        this.appendNotifyChannels(formData);
      }
      
      const response = await axios.post(CURR_API + "/tswebhook.php", formData);
      
      if (response.data.message === "Inserted") {
        // Without the video on the message the composer stays as it is.
        if (!this.verifyVideoAttached(response.data, this.composeVideoUids())) return;
        const participantCount = response.data.participants_notified || this.selectedRecipients.length;
        alert(`Group chat created with ${participantCount} recipient${participantCount !== 1 ? 's' : ''}!`);
        this.clearMessage();
        this.setActiveView('inbox');
        await this.fetchMessages();
        this.sortConversationThreads();
      } else {
        alert(refusalMessage(response.data) || "Error creating group chat. Please try again.");
        console.error('Group message error:', response.data);
      }
    },
    
    clearMessage() {
      this.newMessage = {
        searchTerm: '',
        subject: '',
        message: ''
      };
      this.selectedRecipients = [];
      this.sendSeparately = false; // Reset to default
      this.notifyRecipients = false;
      this.resetNotifyChannelState();
      this.searchResults = [];
      this.showContactSelection = false;
      this.sharedPatients = [];
      this.selectedPatientFocusId = '';
      this.sharedPatientsLoading = false;
      this.clearAllAttachments();
    },
    
    async sendQuickReply() {
      // Enter in the box calls this too: a second Enter while the request is out is the same message.
      if (!this.canSendQuickReply || !this.selectedThread || !this.beginSend('quickReply')) return;
      
      console.log('Sending quick reply to thread:', this.selectedThread);
      
      try {
        const formData = new FormData();
        
        // Handle group chat replies vs individual replies
        if (this.selectedThread.isGroup) {
          console.log('Sending group chat reply to existing group:', this.selectedThread.groupId);
          
          // Use group message API
          formData.append("API_Meth", "SendGroupMessage");
          
          // Get all participants except sender for group reply
          const recipientIds = this.selectedThread.participants.filter(id => id != this.messagingIdentity.subAccountNumber);
          const allParticipants = this.selectedThread.participants;
          
          formData.append("sendersubaccountnumber", this.messagingIdentity.subAccountNumber);
          formData.append("senderaccountnunber", this.messagingIdentity.accountNumber);
          formData.append("participants", recipientIds.join(','));
          formData.append("all_participants", allParticipants.join(','));
          formData.append("group_id", this.selectedThread.groupId); // CRUCIAL: Reuse existing group ID
          formData.append("message", this.quickReplyMessageBody());
          formData.append("subject", this.replySubject(this.selectedThread.lastMessage?.subject, 'Group Message'));
          formData.append("status", "NEW");
          formData.append("senderName", this.messagingIdentity.senderName);
          this.appendQuickReplyAttachment(formData);
          
        } else {
          console.log('Sending individual reply');
          
          // Use individual message API
          formData.append("API_Meth", "SendInternalMessages");
          formData.append("recieversubaccountnumber", this.selectedThread.otherParticipantId);
          formData.append("sendersubaccountnumber", this.messagingIdentity.subAccountNumber);
          formData.append("senderaccountnunber", this.messagingIdentity.accountNumber);
          formData.append("additionalcontacts", '');
          formData.append("internalcontacts", this.selectedThread.otherParticipantId);
          formData.append("message", this.quickReplyMessageBody());
          formData.append("subject", this.replySubject(this.selectedThread.lastMessage?.subject));
          formData.append("status", "NEW");
          formData.append("senderName", this.messagingIdentity.senderName);
          this.appendQuickReplyAttachment(formData);
          // Keep thread's patient focus on quick replies too.
          if (this.selectedThread.patientFocusId) {
            formData.append("patient_focus_id", this.selectedThread.patientFocusId);
          }
        }
        if (this.notifyRecipients) {
          formData.append("notify_recipients", "1");
          this.appendNotifyChannels(formData);
        }
        
        const response = await axios.post(CURR_API + "/tswebhook.php", formData);
        
        console.log('Quick reply response:', response.data);
        
        if (response.data && (response.data.message === "Inserted" || response.data.status === "200-Successfull")) {
          if (!this.verifyVideoAttached(response.data, this.quickReplyVideoUids())) return;
          // Clear the reply input
          this.quickReplyMessage = '';
          this.clearQuickReplyAttachment();
          this.notifyRecipients = false;
          this.resetNotifyChannelState();
          
          // Refresh messages to show the new reply
          await this.fetchMessages();
          
          // Find and reselect the current thread
          const updatedThread = this.conversationThreads.find(t => t.id === this.selectedThread.id);
          if (updatedThread) {
            this.selectedThread = updatedThread;
            
            // Re-sort conversation list since we added a new message (lastMessage changed)
            this.sortConversationThreads();
            
            // Scroll to bottom to show the new message
            this.$nextTick(() => {
              this.scrollToBottom();
            });
          }
        } else {
          alert(refusalMessage(response.data) || "Error sending message. Please try again.");
        }
      } catch (error) {
        console.error('Error sending quick reply:', error);
        alert(refusalMessage(error?.response?.data) || "Error sending reply. Please try again.");
      } finally {
        this.endSend('quickReply');
      }
    },
    
    // Utility Functions
    scrollToBottom() {
      const messagesContainer = document.querySelector('.mobile-chat-messages');
      if (messagesContainer) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
        console.log('Scrolled to bottom of messages');
      }
    },
    // msgTimestamp (UTC) → local day label + time for the viewer.
    formatDate(messageOrRaw) {
      return formatMessageWhen(messageOrRaw);
    },
    
    openContactSelection() {
      this.showContactSelection = true;
    },
    
    clearAllRecipients() {
      this.selectedRecipients = [];
    },
    
    getRecipientIcon(recipient) {
      if (recipient.isExternal) return ['fas', 'user'];
      
      const iconMap = {
        'administrator': ['fas', 'user-tie'],
        'nurse-manager': ['fas', 'user-nurse'],
        'provider': ['fas', 'user-md'],
        'registered-nurse': ['fas', 'user-nurse'],
        'licensed-practical-nurse': ['fas', 'user-nurse'],
        'home-health-aide': ['fas', 'user-nurse'],
        'nursing-agency': ['fas', 'building'],
        'home-healthcare-agency': ['fas', 'building'],
        'care-management-agency': ['fas', 'building'],
        'patients': ['fas', 'user']
      };
      
      return iconMap[recipient.accountType] || ['fas', 'user'];
    },
    
    getRecipientRole(recipient) {
      if (recipient.isExternal) return 'External Contact';
      
      const roleMap = {
        'administrator': 'Administrator',
        'nurse-manager': 'Nurse Manager',
        'provider': 'Provider',
        'registered-nurse': 'Registered Nurse',
        'licensed-practical-nurse': 'Licensed Practical Nurse',
        'home-health-aide': 'Home Health Aide',
        'nursing-agency': 'Nursing Agency',
        'home-healthcare-agency': 'Home Healthcare Agency',
        'care-management-agency': 'Care Management Agency',
        'patients': 'Patient'
      };
      
      return roleMap[recipient.accountType] || recipient.accountType?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Contact';
    },
    
    truncateMessage(message, length = 50) {
      const text = String(message || "");
      if (!text || text === "(Attachment)") return "";
      return text.length > length ? text.substring(0, length) + '...' : text;
    },

    threadPreviewText(thread) {
      const msg = thread?.lastMessage;
      const text = this.truncateMessage(msg?.message);
      if (text) return text;
      // Attachment-only messages: show kind/count instead of blank preview.
      if (this.hasMessageAttachment(msg)) {
        return this.attachmentPreviewLabel(msg);
      }
      return "";
    }
  }
};
</script>

<style scoped>
/* Mobile Message Widget Styles */
.mobile-message-widget {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  z-index: 9999999999 !important;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  display: block !important; /* Ensure it's visible by default */
  visibility: visible !important;
  pointer-events: none !important; /* Allow clicks to pass through by default */
}

/* Mobile Header Bar (Collapsed State) */
.mobile-header-bar {
  background: #24555b;
  color: white;
  padding: 8px 12px;
  box-shadow: none;
  cursor: pointer;
  transition: background 0.15s ease;
  z-index: 9999999999 !important;
  position: fixed !important;
  bottom: 0 !important;
  left: 0 !important;
  right: 0 !important;
  pointer-events: auto !important; /* Enable clicks on header bar */
}

.mobile-header-bar:active {
  transform: scale(0.98);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.chat-icon {
  font-size: 18px;
}

.header-text {
  font-size: 16px;
  font-weight: 600;
  flex: 1;
}

.unread-badge {
  background: #ff4757;
  color: white;
  border-radius: 12px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 600;
  min-width: 20px;
  text-align: center;
}

/* Full Screen Mobile Widget */
.mobile-widget-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  display: flex;
  flex-direction: column;
  z-index: 9999999999 !important;
  pointer-events: auto !important; /* Enable clicks when expanded */
}

/* Mobile Widget Header */
.mobile-widget-header {
  background: #24555b;
  color: white;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.back-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 10px;
  border-radius: 50%;
  transition: background-color 0.2s;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.05);
}

.header-title {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  font-size: 18px;
  font-weight: 600;
}

.header-icon {
  font-size: 20px;
}

.compose-btn {
  background: none;
  border: none;
  color: white;
  font-size: 18px;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.compose-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Mobile Navigation */
.mobile-nav {
  background: #f8f9fa;
  padding: 8px 16px;
  display: flex;
  gap: 8px;
  border-bottom: 1px solid #e9ecef;
}

.nav-btn {
  background: none;
  border: none;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  justify-content: center;
}

.nav-btn.active {
  background: #007bff;
  color: white;
}

.nav-btn:hover:not(.active) {
  background: #e9ecef;
}

.nav-badge {
  background: #ff4757;
  color: white;
  border-radius: 10px;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: 600;
  min-width: 16px;
  text-align: center;
}

/* Mobile Content */
.mobile-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Mobile Inbox View */
.mobile-inbox-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.mobile-search-bar {
  padding: 16px;
  background: white;
  border-bottom: 1px solid #e9ecef;
  position: relative;
}

.search-icon {
  position: absolute;
  left: 24px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  font-size: 16px;
}

.mobile-search-input {
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 1px solid #ced4da;
  border-radius: 8px;
  font-size: 16px;
  outline: none;
  transition: border-color 0.2s;
}

.mobile-search-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Mobile Message List */
.mobile-message-list {
  flex: 1;
  overflow-y: auto;
  background: white;
}

.mobile-thread-item {
  display: flex;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #f1f3f4;
  cursor: pointer;
  transition: background-color 0.2s;
  gap: 12px;
}

.mobile-thread-item:hover {
  background: #f8f9fa;
}

.mobile-thread-item.unread {
  background: #f0f8ff;
}

.mobile-thread-avatar {
  width: 48px;
  height: 48px;
  background: #e9ecef;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-size: 20px;
  flex-shrink: 0;
}

.mobile-thread-content {
  flex: 1;
  min-width: 0;
}

.mobile-thread-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.mobile-participant-name {
  font-weight: 600;
  font-size: 16px;
  color: #212529;
  display: flex;
  align-items: center;
  gap: 6px;
}

.group-icon {
  font-size: 14px;
  color: #007bff;
}

.mobile-thread-date {
  font-size: 12px;
  color: #6c757d;
  flex-shrink: 0;
}

.mobile-thread-preview {
  font-size: 14px;
  color: #6c757d;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

.mobile-thread-preview-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.hidden-file-input {
  display: none;
}

.mobile-attachment-compose,
.mobile-quick-reply-attachment,
.mobile-attachment-compose-list,
.mobile-message-attachments-list,
.mobile-thread-attachment-thumbs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}

.mobile-attachment-compose-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

/* Shown while a video uploads: a phone that locks pauses it. */
.mobile-keep-open {
  flex-basis: 100%;
  margin: 0;
  font-size: 13px;
  color: #6c757d;
}

.mobile-thread-preview-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mobile-attach-btn,
.mobile-clear-attach-btn,
.mobile-quick-attach-btn {
  border: 1px solid #ced4da;
  background: #fff;
  color: #495057;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
}

.mobile-quick-attach-btn {
  padding: 10px 12px;
  flex-shrink: 0;
}

.mobile-unread-badge {
  background: #007bff;
  color: white;
  border-radius: 12px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 600;
  min-width: 20px;
  text-align: center;
  flex-shrink: 0;
}

/* Mobile Empty State */
.mobile-empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  color: #6c757d;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.mobile-empty-state p {
  font-size: 16px;
  margin: 0;
}

/* Mobile Compose View */
.mobile-compose-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: white;
}

.mobile-compose-header {
  background: #f8f9fa;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  border-bottom: 1px solid #e9ecef;
}

.compose-title {
  flex: 1;
  font-size: 18px;
  font-weight: 600;
  color: #212529;
}

.send-btn {
  background: #007bff;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
}

.send-btn:hover:not(:disabled) {
  background: #0056b3;
}

.send-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

/* Mobile Recipient Section */
.mobile-recipient-section {
  padding: 16px;
  border-bottom: 1px solid #e9ecef;
}

.recipient-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.recipient-label {
  font-weight: 600;
  color: #212529;
  font-size: 16px;
}

.add-recipients-btn {
  background: #e9ecef;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  color: #495057;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
}

.add-recipients-btn:hover {
  background: #ced4da;
}

.mobile-selected-recipients {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.mobile-recipient-tag {
  background: #007bff;
  color: white;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.remove-recipient-btn {
  background: none;
  border: none;
  color: white;
  font-size: 12px;
  cursor: pointer;
  padding: 2px;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.remove-recipient-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Mobile Message Form */
.mobile-message-form {
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mobile-subject-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ced4da;
  border-radius: 8px;
  font-size: 16px;
  outline: none;
  transition: border-color 0.2s;
}

.mobile-subject-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.mobile-patient-focus {
  margin-bottom: 12px;
}

.mobile-patient-focus-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 6px;
}

.mobile-patient-focus-select {
  width: 100%;
  border: 1px solid #ced4da;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 15px;
  background: #fff;
}

.mobile-patient-focus-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #888;
}

.mobile-subject-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.mobile-subject-row .mobile-subject-input {
  flex: 1;
  min-width: 0;
}

.mobile-patient-focus-prefix {
  flex-shrink: 0;
  background: #eef1ff;
  color: #4c5fd7;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.mobile-thread-patient-focus,
.thread-patient-focus {
  color: #4c5fd7;
  font-size: 12px;
  font-weight: 600;
}

.mobile-message-textarea {
  flex: 1;
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ced4da;
  border-radius: 8px;
  font-size: 16px;
  font-family: inherit;
  resize: none;
  outline: none;
  transition: border-color 0.2s;
  min-height: 120px;
}

.mobile-message-textarea:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Mobile Send Options */
.mobile-send-options {
  padding: 16px;
  border-top: 1px solid #e9ecef;
}

.mobile-checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #495057;
  cursor: pointer;
}

.mobile-checkbox {
  width: 16px;
  height: 16px;
  accent-color: #007bff;
}

.mobile-select-channels-btn {
  display: inline-flex;
  align-items: center;
  margin-top: 10px;
  margin-left: 24px;
  background: #24555b;
  color: white;
  border: none;
  padding: 8px 14px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.mobile-select-channels-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(36, 85, 91, 0.35);
}

/* Mobile Thread View */
.mobile-thread-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: white;
}

.mobile-thread-header {
  background: #f8f9fa;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  border-bottom: 1px solid #e9ecef;
}

.thread-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.thread-title {
  font-size: 18px;
  font-weight: 600;
  color: #212529;
}

.thread-subtitle {
  font-size: 14px;
  color: #6c757d;
}

.participants-btn {
  background: none;
  border: none;
  color: #6c757d;
  font-size: 18px;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.participants-btn:hover {
  background: #e9ecef;
}

/* Mobile Participants List */
.mobile-participants-list {
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.participants-header {
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  border-bottom: 1px solid #e9ecef;
}

.mobile-participants {
  padding: 8px 0;
}

.mobile-participant-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  font-size: 14px;
  color: #495057;
}

.mobile-participant-item.current-user {
  background: #e3f2fd;
  color: #1976d2;
}

.participant-icon {
  font-size: 16px;
  width: 20px;
  text-align: center;
}

.participant-name {
  flex: 1;
}

/* Mobile Chat Messages */
.mobile-chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: #f8f9fa;
  height: 0; /* This is crucial for flex scrolling to work */
  min-height: 0; /* This is crucial for flex scrolling to work */
}

.mobile-message-bubble {
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
}

.mobile-message-bubble.own-message {
  align-items: flex-end;
}

.mobile-message-content {
  max-width: 80%;
  padding: 12px 16px;
  border-radius: 18px;
  background: white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.mobile-message-bubble.own-message .mobile-message-content {
  background: #007bff;
  color: white;
}

.mobile-message-text {
  font-size: 16px;
  line-height: 1.4;
  word-wrap: break-word;
  margin-bottom: 4px;
}

.mobile-message-sender {
  font-size: 12px;
  font-weight: 500;
  color: #6c757d;
  margin-bottom: 4px;
  opacity: 0.8;
}

.mobile-message-timestamp {
  font-size: 12px;
  opacity: 0.7;
  text-align: right;
}

/* Mobile Quick Reply */
.mobile-quick-reply {
  background: white;
  border-top: 1px solid #e9ecef;
  padding: 16px;
}

.mobile-quick-reply-notify-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
}

.mobile-quick-reply-notify {
  margin-bottom: 0;
  font-size: 0.85rem;
}

.mobile-select-channels-btn--compact {
  margin-top: 0;
  margin-left: 0;
  padding: 6px 10px;
  font-size: 12px;
}

.mobile-reply-form {
  margin: 0;
}

.mobile-reply-input-container {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.mobile-quick-reply-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.mobile-quick-reply-textarea {
  width: 100%;
  flex: 1;
  border: 1px solid #ced4da;
  border-radius: 20px;
  padding: 12px 16px;
  resize: none;
  font-family: inherit;
  font-size: 16px;
  line-height: 1.4;
  outline: none;
  transition: border-color 0.2s;
  min-height: 44px;
  max-height: 120px;
}

.mobile-quick-reply-textarea:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.mobile-quick-send-btn {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: #007bff;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
  flex-shrink: 0;
}

.mobile-quick-send-btn:hover:not(:disabled) {
  background: #0056b3;
}

.mobile-quick-send-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

/* Mobile Contact Modal */
.mobile-contact-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2147483647;
  display: flex;
  align-items: flex-end;
  pointer-events: auto !important; /* Enable clicks on modal */
}

.mobile-modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  pointer-events: auto !important; /* Enable clicks on overlay */
}

.mobile-modal-content {
  position: relative;
  background: white;
  width: 100%;
  height: 80%;
  border-radius: 16px 16px 0 0;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease-out;
  pointer-events: auto !important; /* Enable clicks on modal content */
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

.mobile-modal-header {
  padding: 16px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  gap: 16px;
}

.close-modal-btn {
  background: none;
  border: none;
  color: #6c757d;
  font-size: 18px;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.close-modal-btn:hover {
  background: #e9ecef;
}

.modal-title {
  flex: 1;
  font-size: 18px;
  font-weight: 600;
  color: #212529;
}

.confirm-btn {
  background: #007bff;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.confirm-btn:hover:not(:disabled) {
  background: #0056b3;
}

.confirm-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

.mobile-contact-list {
  flex: 1;
  overflow: hidden;
}

/* Responsive adjustments for very small screens */
@media (max-width: 360px) {
  .mobile-widget-header {
    padding: 12px;
  }
  
  .header-title {
    font-size: 16px;
  }
  
  .mobile-thread-item {
    padding: 12px;
  }
  
  .mobile-thread-avatar {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }
  
  .mobile-participant-name {
    font-size: 14px;
  }
  
  .mobile-thread-preview {
    font-size: 13px;
  }
}

/* Landscape orientation adjustments */
@media (orientation: landscape) and (max-height: 500px) {
  .mobile-modal-content {
    height: 90%;
  }
  
  .mobile-widget-header {
    padding: 12px 16px;
  }
  
  .mobile-nav {
    padding: 6px 16px;
  }
  
  .nav-btn {
    padding: 8px 12px;
    font-size: 13px;
  }
}

/* High DPI displays */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .mobile-thread-item {
    border-bottom-width: 0.5px;
  }
  
  .mobile-widget-header {
    border-bottom-width: 0.5px;
  }
}

/* Mobile-specific z-index overrides to ensure widget is always on top */
.mobile-message-widget {
  z-index: 9999999999 !important;
}

.mobile-widget-fullscreen {
  z-index: 9999999999 !important;
}

.mobile-contact-modal {
  z-index: 9999999999 !important;
}
/* Remove, beside an attachment its sender may take back (DeleteMessageAttachment). */
.attachment-with-actions {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}
.attachment-remove {
  background: none;
  border: none;
  padding: 0;
  color: #b42318;
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;
}
</style>
